/* Copyright (c) 2026 Hyegro Pty Ltd. All rights reserved. */

/**
 * Viewings — calendar (FullCalendar, already a dependency) and list.
 *
 * GET /re/viewings (from, to, project_id, assigned_to, status), POST /re/viewings,
 * PUT /re/viewings/{id} (edit, mark attended / no-show).
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import interactionPlugin from '@fullcalendar/interaction';
import {
  Alert, Dialog, DialogActions, DialogContent, DialogTitle, Grid, Icon, MenuItem, TextField, ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import MDBox from 'components/MDBox';
import MDButton from 'components/MDButton';
import MDTypography from 'components/MDTypography';
import { usePermission } from 'auth/usePermission';
import RePage, { FilterBar, LoadError, RePanel, useSnack } from './components/RePage';
import ReStatusChip from './components/ReStatusChip';
import ReTable from './components/ReTable';
import {
  ContactPicker, LeadPicker, RePicker, UserPicker, VocabSelect,
} from './components/Pickers';
import { DateField } from './components/Widgets';
import { useIsReV2 } from '../v2/parts';
import SegmentedControl from 'components/v2/SegmentedControl';
import { CalendarDays, List } from 'lucide-react';
import { TONES, toneFor } from './components/statusColors';
import {
  INTEREST_LEVELS, VIEWING_STATUSES, VIEWING_TYPES, addDaysIso, errorMessage, formatDateTime, humanize,
  localInputToIso, todayIso, toLocalInputDateTime, useReApi, useReList,
} from './api';

const MODE_OPTIONS = [{ value: 'calendar', label: 'Calendar', icon: CalendarDays }, { value: 'list', label: 'List', icon: List }];

const emptyForm = (start) => ({
  contact_id: null, lead_id: null, project_id: null, unit_id: null, assigned_to: null, broker_firm_id: null,
  scheduled_at: toLocalInputDateTime(start || null), duration_minutes: 60, viewing_type: 'SITE_VISIT', location: '',
  status: 'SCHEDULED',
});

function ViewingDialog({ open, viewing, defaultStart, onClose, onSaved, onAttended }) {
  const { api } = useReApi();
  const [form, setForm] = useState(emptyForm());
  const [err, setErr] = useState(null);
  const [saving, setSaving] = useState(false);
  const isEdit = Boolean(viewing?.id);

  useEffect(() => {
    if (!open) return;
    setErr(null);
    if (viewing) {
      setForm({
        contact_id: viewing.contact_id || null, lead_id: viewing.lead_id || null, project_id: viewing.project_id || null,
        unit_id: viewing.unit_id || null, assigned_to: viewing.assigned_to || null, broker_firm_id: viewing.broker_firm_id || null,
        scheduled_at: toLocalInputDateTime(viewing.scheduled_at), duration_minutes: viewing.duration_minutes || 60,
        viewing_type: viewing.viewing_type || 'SITE_VISIT', location: viewing.location || '', status: viewing.status || 'SCHEDULED',
      });
    } else {
      setForm(emptyForm(defaultStart));
    }
  }, [open, viewing, defaultStart]);

  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v && v.target ? v.target.value : v }));

  const submit = async () => {
    if (!form.contact_id && !form.lead_id) {
      setErr('Choose a contact or a lead.');
      return;
    }
    if (!form.project_id && !form.unit_id) {
      setErr('Choose a project or a unit.');
      return;
    }
    const at = localInputToIso(form.scheduled_at);
    if (!at) {
      setErr('Choose a date and time.');
      return;
    }
    setSaving(true);
    const body = {
      ...form,
      scheduled_at: at,
      duration_minutes: parseInt(form.duration_minutes, 10) || 60,
      location: form.location || null,
    };
    try {
      if (isEdit) await api.put(`/re/viewings/${viewing.id}`, body);
      else await api.post('/re/viewings', body);
      onSaved(isEdit ? 'Viewing updated' : 'Viewing scheduled');
    } catch (e) {
      setErr(errorMessage(e, 'Could not save the viewing'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle>{isEdit ? 'Edit viewing' : 'Schedule viewing'}</DialogTitle>
      <DialogContent>
        {err && <Alert severity="error" sx={{ mb: 2 }}>{err}</Alert>}
        <Grid container spacing={2} sx={{ pt: 1 }}>
          <Grid item xs={12} md={6}><ContactPicker label="Contact" value={form.contact_id} onChange={set('contact_id')} allowCreate sx={{ width: '100%' }} /></Grid>
          <Grid item xs={12} md={6}><LeadPicker label="Lead" value={form.lead_id} onChange={set('lead_id')} sx={{ width: '100%' }} /></Grid>
          <Grid item xs={12} md={6}>
            <RePicker path="/re/projects" label="Project" value={form.project_id} onChange={(v) => setForm((f) => ({ ...f, project_id: v, unit_id: null }))} sx={{ width: '100%' }} />
          </Grid>
          <Grid item xs={12} md={6}>
            <RePicker path="/re/units" label="Unit (optional)" value={form.unit_id} onChange={set('unit_id')} params={{ project_id: form.project_id }} sx={{ width: '100%' }} />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField fullWidth size="small" type="datetime-local" label="When" value={form.scheduled_at} onChange={set('scheduled_at')} InputLabelProps={{ shrink: true }} />
          </Grid>
          <Grid item xs={6} md={2}>
            <TextField fullWidth size="small" type="number" label="Minutes" value={form.duration_minutes} onChange={set('duration_minutes')} />
          </Grid>
          <Grid item xs={6} md={3}>
            <TextField select fullWidth size="small" label="Type" value={form.viewing_type} onChange={set('viewing_type')}>
              {VIEWING_TYPES.map((t) => <MenuItem key={t} value={t}>{humanize(t)}</MenuItem>)}
            </TextField>
          </Grid>
          <Grid item xs={12} md={3}>
            <TextField select fullWidth size="small" label="Status" value={form.status} onChange={set('status')}>
              {VIEWING_STATUSES.map((t) => <MenuItem key={t} value={t}>{humanize(t)}</MenuItem>)}
            </TextField>
          </Grid>
          <Grid item xs={12} md={4}><UserPicker label="Agent" value={form.assigned_to} onChange={set('assigned_to')} emptyLabel="Unassigned" sx={{ width: '100%' }} /></Grid>
          <Grid item xs={12} md={4}><RePicker path="/re/broker-firms" label="Broker firm" value={form.broker_firm_id} onChange={set('broker_firm_id')} sx={{ width: '100%' }} /></Grid>
          <Grid item xs={12} md={4}><TextField fullWidth size="small" label="Location / meeting point" value={form.location} onChange={set('location')} /></Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        {onAttended && (
          <MDButton variant="outlined" color="success" onClick={onAttended} sx={{ mr: 'auto' }}>Mark attended</MDButton>
        )}
        <MDButton variant="text" color="secondary" onClick={onClose}>Cancel</MDButton>
        <MDButton variant="gradient" color="info" onClick={submit} disabled={saving}>{saving ? 'Saving…' : 'Save'}</MDButton>
      </DialogActions>
    </Dialog>
  );
}

function AttendedDialog({ viewing, onClose, onSaved }) {
  const { api } = useReApi();
  const [form, setForm] = useState({ interest_level: 'WARM', feedback: '', next_step: '' });
  const [err, setErr] = useState(null);
  useEffect(() => {
    if (viewing) setForm({ interest_level: viewing.interest_level || 'WARM', feedback: viewing.feedback || '', next_step: viewing.next_step || '' });
  }, [viewing]);
  const submit = async () => {
    try {
      await api.put(`/re/viewings/${viewing.id}`, {
        status: 'COMPLETED', attended_at: new Date().toISOString(), ...form,
      });
      onSaved('Viewing marked as attended');
    } catch (e) {
      setErr(errorMessage(e, 'Could not update the viewing'));
    }
  };
  return (
    <Dialog open={Boolean(viewing)} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>Mark attended</DialogTitle>
      <DialogContent>
        {err && <Alert severity="error" sx={{ mb: 2 }}>{err}</Alert>}
        <MDBox display="flex" flexDirection="column" gap={2} pt={1}>
          <ToggleButtonGroup exclusive size="small" value={form.interest_level} onChange={(e, v) => v && setForm({ ...form, interest_level: v })}>
            {INTEREST_LEVELS.map((l) => <ToggleButton key={l} value={l}>{humanize(l)}</ToggleButton>)}
          </ToggleButtonGroup>
          <TextField multiline minRows={3} size="small" label="Feedback" value={form.feedback} onChange={(e) => setForm({ ...form, feedback: e.target.value })} />
          <TextField size="small" label="Next step" value={form.next_step} onChange={(e) => setForm({ ...form, next_step: e.target.value })} placeholder="Send floor plans, second viewing, hold unit…" />
        </MDBox>
      </DialogContent>
      <DialogActions>
        <MDButton variant="text" color="secondary" onClick={onClose}>Cancel</MDButton>
        <MDButton variant="gradient" color="success" onClick={submit}>Save</MDButton>
      </DialogActions>
    </Dialog>
  );
}

const viewingTitle = (v) => [v.contact_name || v.lead_name, v.unit_number ? `Unit ${v.unit_number}` : v.project_name].filter(Boolean).join(' · ') || `Viewing #${v.id}`;

export default function ViewingsCalendar() {
  const [params] = useSearchParams();
  const [snack, notify] = useSnack();
  const { api } = useReApi();
  const { canCreate, canEdit } = usePermission('re_viewings');
  const [mode, setMode] = useState('calendar');
  const isV2 = useIsReV2();
  const [range, setRange] = useState({ from: addDaysIso(-7), to: addDaysIso(35) });
  const [projectId, setProjectId] = useState(params.get('project_id') ? Number(params.get('project_id')) : null);
  const [agent, setAgent] = useState(null);
  const [status, setStatus] = useState(null);
  const [listFrom, setListFrom] = useState(todayIso());
  const [listTo, setListTo] = useState(addDaysIso(30));
  const [editing, setEditing] = useState(null); // {viewing?, start?}
  const [attended, setAttended] = useState(null);

  const filters = { project_id: projectId, assigned_to: agent, status };
  const cal = useReList(mode === 'calendar' ? '/re/viewings' : null, { ...filters, ...range, sort_by: 'scheduled_at', sort_order: 'asc' }, { pageSize: 200 });
  const list = useReList(mode === 'list' ? '/re/viewings' : null, { ...filters, from: listFrom, to: listTo, sort_by: 'scheduled_at', sort_order: 'asc' });

  const events = useMemo(() => cal.items.map((v) => {
    const t = TONES[toneFor('viewing', v.status)];
    const start = new Date(v.scheduled_at);
    return {
      id: String(v.id),
      title: viewingTitle(v),
      start,
      end: new Date(start.getTime() + (v.duration_minutes || 60) * 60000),
      backgroundColor: t.background,
      borderColor: t.dot,
      textColor: t.text,
      extendedProps: { viewing: v },
    };
  }), [cal.items]);

  const onDatesSet = useCallback((info) => {
    const from = info.startStr.slice(0, 10);
    const to = info.endStr.slice(0, 10);
    setRange((r) => (r.from === from && r.to === to ? r : { from, to }));
  }, []);

  const reloadAll = () => { cal.reload(); list.reload(); };
  const onSaved = (msg) => {
    setEditing(null);
    setAttended(null);
    notify(msg);
    reloadAll();
  };

  const quickStatus = async (v, st) => {
    try {
      await api.put(`/re/viewings/${v.id}`, { status: st });
      notify(`Viewing ${humanize(st).toLowerCase()}`);
      reloadAll();
    } catch (e) {
      notify.error(e, 'Could not update the viewing');
    }
  };

  const openable = (v) => canEdit && ['SCHEDULED', 'CONFIRMED'].includes(v.status);

  return (
    <RePage
      title="Viewings"
      subtitle="Site visits, show-flat and unit viewings"
      actions={(
        <>
          {isV2 ? (
            <SegmentedControl label="View" options={MODE_OPTIONS} value={mode} onChange={setMode} />
          ) : (
            <ToggleButtonGroup exclusive size="small" value={mode} onChange={(e, v) => v && setMode(v)}>
              <ToggleButton value="calendar"><Icon fontSize="small">calendar_month</Icon>&nbsp;Calendar</ToggleButton>
              <ToggleButton value="list"><Icon fontSize="small">list</Icon>&nbsp;List</ToggleButton>
            </ToggleButtonGroup>
          )}
          {canCreate && (
            <MDButton variant="gradient" color="info" size="small" onClick={() => setEditing({ viewing: null, start: null })}>
              <Icon>add</Icon>&nbsp;New viewing
            </MDButton>
          )}
        </>
      )}
    >
      <FilterBar>
        <RePicker path="/re/projects" label="Project" value={projectId} onChange={setProjectId} />
        <UserPicker label="Agent" value={agent} onChange={setAgent} />
        <VocabSelect label="Status" value={status} onChange={setStatus} values={VIEWING_STATUSES} />
        {mode === 'list' && (
          <>
            <DateField label="From" value={listFrom} onChange={setListFrom} />
            <DateField label="To" value={listTo} onChange={setListTo} />
          </>
        )}
      </FilterBar>

      {mode === 'calendar' ? (
        <>
          <LoadError error={cal.error} onRetry={cal.reload} />
          {cal.total > cal.items.length && (
            <Alert severity="info" sx={{ mb: 1 }}>{`Showing the first ${cal.items.length} of ${cal.total} viewings in this range. Narrow the filters to see all.`}</Alert>
          )}
          <RePanel>
            <MDBox
              sx={{
                '& .fc': { fontSize: '0.8rem' },
                '& .fc .fc-toolbar-title': { fontSize: '1.1rem', fontWeight: 600 },
                '& .fc .fc-button': { textTransform: 'capitalize', fontSize: '0.75rem', py: 0.5 },
                '& .fc .fc-event': { cursor: 'pointer', borderLeftWidth: 3 },
              }}
            >
              <FullCalendar
                plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin]}
                initialView="timeGridWeek"
                headerToolbar={{ left: 'prev,next today', center: 'title', right: 'timeGridWeek,dayGridMonth' }}
                slotMinTime="07:00:00"
                slotMaxTime="22:00:00"
                allDaySlot={false}
                height="auto"
                nowIndicator
                firstDay={1}
                events={events}
                datesSet={onDatesSet}
                selectable={canCreate}
                select={(info) => setEditing({ viewing: null, start: info.start })}
                eventClick={(info) => {
                  if (canEdit) setEditing({ viewing: info.event.extendedProps.viewing });
                }}
              />
            </MDBox>
          </RePanel>
        </>
      ) : (
        <>
          <LoadError error={list.error} onRetry={list.reload} />
          <RePanel p={0}>
            <ReTable
              loading={list.loading}
              rows={list.items}
              emptyMessage="No viewings in this range."
              onRowClick={canEdit ? (v) => setEditing({ viewing: v }) : null}
              total={list.total}
              page={list.page}
              pageSize={list.pageSize}
              onPageChange={list.setPage}
              onPageSizeChange={list.setPageSize}
              columns={[
                { key: 'scheduled_at', label: 'When', render: (v) => formatDateTime(v.scheduled_at) },
                { key: 'who', label: 'Client', render: (v) => v.contact_name || v.lead_name || '—' },
                { key: 'project_name', label: 'Project' },
                { key: 'unit_number', label: 'Unit' },
                { key: 'viewing_type', label: 'Type', render: (v) => humanize(v.viewing_type) || '—' },
                { key: 'assigned_to_name', label: 'Agent' },
                { key: 'broker_firm_name', label: 'Broker' },
                { key: 'interest_level', label: 'Interest', render: (v) => (v.interest_level ? <ReStatusChip vocab="interest" status={v.interest_level} size="xs" /> : '—') },
                { key: 'status', label: 'Status', render: (v) => <ReStatusChip vocab="viewing" status={v.status} size="xs" /> },
                {
                  key: 'actions', label: '', align: 'right',
                  render: (v) => (openable(v) ? (
                    <MDBox display="flex" gap={0.5} justifyContent="flex-end" onClick={(e) => e.stopPropagation()}>
                      <MDButton size="small" variant="text" color="success" onClick={() => setAttended(v)}>Attended</MDButton>
                      <MDButton size="small" variant="text" color="error" onClick={() => quickStatus(v, 'NO_SHOW')}>No-show</MDButton>
                    </MDBox>
                  ) : null),
                },
              ]}
            />
          </RePanel>
        </>
      )}

      <ViewingDialog
        open={Boolean(editing)}
        viewing={editing?.viewing || null}
        defaultStart={editing?.start || null}
        onClose={() => setEditing(null)}
        onSaved={onSaved}
        onAttended={editing?.viewing && openable(editing.viewing) ? () => { setAttended(editing.viewing); setEditing(null); } : null}
      />
      <AttendedDialog viewing={attended} onClose={() => setAttended(null)} onSaved={onSaved} />
      <MDTypography variant="caption" color="text" component="p" mt={1}>
        Tip: drag across the week view to schedule a viewing at that time; click a viewing to edit it or mark it attended.
      </MDTypography>
      {snack}
    </RePage>
  );
}
